import { useState } from "react";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatApiErrorDetail(detail) {
  if (detail == null) return "The ritual failed. Try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const inputCls =
  "w-full font-courier text-sm bg-void border-2 border-input text-paper px-3 py-2.5 focus:outline-none focus:border-crimson placeholder:text-paper/30";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      setError("The two passwords do not match.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await axios.post(`${API}/auth/reset-password`, { token, password });
      setDone(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="reset-password-page" className="min-h-screen bg-void newsprint-grain">
      <header className="border-b border-blooddeep/40 bg-void/85 backdrop-blur-xl">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 h-16 flex items-center justify-between">
          <a href="/" data-testid="reset-brand" className="flex items-center gap-2 group">
            <img src="/covers/atrocity-logo.webp" alt="Atrocity Comix logo" className="h-6 w-6 rounded-full transition-transform duration-200 group-hover:rotate-12" />
            <span className="font-display text-sm sm:text-base text-paper tracking-wide">
              ATROCITY <span className="text-crimson">COMIX</span>
            </span>
          </a>
          <a
            href="/account"
            data-testid="reset-back-link"
            className="flex items-center gap-2 font-impact uppercase tracking-wider text-xs text-paper/70 transition-colors duration-150 hover:text-crimson"
          >
            <ArrowLeft size={14} /> To Your Crypt
          </a>
        </div>
      </header>

      <div className="mx-auto max-w-md px-4 sm:px-6 py-20">
        <p className="font-marker text-paper text-base sm:text-lg mb-3 -rotate-1 inline-block">rise again</p>
        <h1 className="font-display text-3xl sm:text-4xl uppercase text-paper mb-8">
          NEW <span className="text-crimson">PASSWORD</span>
        </h1>
        <div className="border-2 border-blooddeep bg-pulp p-6 sm:p-8">
          {done ? (
            <div className="text-center">
              <p data-testid="reset-success-message" className="font-type text-sm text-paper/70 mb-6">
                Your password is reborn. The crypt recognizes you once more.
              </p>
              <a
                data-testid="reset-login-link"
                href="/account"
                className="inline-block font-impact uppercase tracking-wider bg-blooddeep text-bone border-2 border-black px-6 py-3.5 ink-shadow crimson-glow transition-all duration-150 hover:bg-crimson"
              >
                Enter the Crypt
              </a>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3">
              <input
                data-testid="reset-new-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="New password (8+ characters)"
                className={inputCls}
              />
              <input
                data-testid="reset-confirm-password-input"
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="Confirm new password"
                className={inputCls}
              />
              {error && (
                <p data-testid="reset-error-message" className="font-type text-xs text-bloodglow">
                  {error}
                </p>
              )}
              <button
                data-testid="reset-submit-button"
                type="submit"
                disabled={busy || !token}
                className="w-full font-impact uppercase tracking-wider bg-blooddeep text-bone border-2 border-black px-6 py-3.5 ink-shadow crimson-glow transition-all duration-150 hover:bg-crimson hover:-translate-y-0.5 active:translate-x-1 active:translate-y-1 disabled:opacity-50"
              >
                {busy ? "Resurrecting..." : "Set New Password"}
              </button>
              {!token && (
                <p className="font-type text-xs text-bloodglow text-center">
                  This link is missing its token. Use the link from your email.
                </p>
              )}
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
